import { API_BASE_URL, ApiError, apiGet, apiPost } from '@/api/http';

import type { AuthErrorResponse, AuthTokenResponse } from '@/api/user';

const BASE = API_BASE_URL + 'rest-auth/mfa';
const PENDING_BASE = API_BASE_URL + 'rest-auth/mfa-pending';

export interface MFAVerifyLoginPayload {
  mfa_token: string;
  otp_token: string;
}

export interface MFASetupData {
  provisioning_uri: string;
  secret: string;
  qr_code: string;
}

export interface MFAStatus {
  enabled: boolean;
  method?: 'email' | 'app' | null;
  can_switch_to_email?: boolean;
  can_enable_email?: boolean;
  backup_codes_remaining: number;
  enforce_after?: string | null;
  enforcement_active?: boolean;
  deferred?: boolean;
  can_postpone?: boolean;
  can_disable_until_enforce?: boolean;
}

const catchAuthError = (error: unknown): AuthErrorResponse | undefined => {
  if (error instanceof ApiError) {
    return { status: error.status, data: error.data };
  }
  return undefined;
};

export const verifyMFALogin = async (
  data: MFAVerifyLoginPayload,
): Promise<AuthTokenResponse | AuthErrorResponse | undefined> => {
  try {
    return await apiPost<AuthTokenResponse>(
      `${API_BASE_URL}rest-auth/mfa-verify/`,
      data,
    );
  } catch (error: unknown) {
    return catchAuthError(error);
  }
};

export const getMFAStatus = () => apiGet<MFAStatus>(`${BASE}/status/`);

export const getMFASetup = () => apiGet<MFASetupData>(`${BASE}/setup/`);

export interface MFAConfirmResponse {
  backup_codes: string[];
}

export const confirmMFASetup = async (
  otp_token: string,
): Promise<MFAConfirmResponse | AuthErrorResponse | undefined> => {
  try {
    return await apiPost<MFAConfirmResponse>(`${BASE}/setup/`, { otp_token });
  } catch (error: unknown) {
    return catchAuthError(error);
  }
};

export const disableMFA = async (
  otp_token: string,
  options?: { until_enforce?: boolean },
): Promise<AuthErrorResponse | undefined> => {
  const body: { otp_token?: string; until_enforce?: boolean } = {};
  if (otp_token) {
    body.otp_token = otp_token;
  }
  if (options?.until_enforce) {
    body.until_enforce = true;
  }
  try {
    await apiPost(`${BASE}/disable/`, body);
    return undefined;
  } catch (error: unknown) {
    return catchAuthError(error);
  }
};

/** Prefer email OTP for future logins (no TOTP enrolled yet). */
export const enableMFAEmail = async (): Promise<
  AuthErrorResponse | undefined
> => {
  try {
    await apiPost(`${BASE}/enable-email/`);
    return undefined;
  } catch (error: unknown) {
    return catchAuthError(error);
  }
};

// ---------------------------------------------------------------------------
// Pending-login method selection (no auth token — uses mfa_token)
// ---------------------------------------------------------------------------

/** Choose email path: sends OTP and returns the mfa_required challenge. */
export const chooseMFAEmail = async (
  mfa_token: string,
): Promise<AuthTokenResponse | AuthErrorResponse | undefined> => {
  try {
    return await apiPost<AuthTokenResponse>(`${PENDING_BASE}/send-email/`, {
      mfa_token,
    });
  } catch (error: unknown) {
    return catchAuthError(error);
  }
};

/** Postpone MFA setup until the global enforcement date; returns a login token. */
export const postponeMFA = async (
  mfa_token: string,
): Promise<AuthTokenResponse | AuthErrorResponse | undefined> => {
  try {
    return await apiPost<AuthTokenResponse>(`${PENDING_BASE}/postpone/`, {
      mfa_token,
    });
  } catch (error: unknown) {
    return catchAuthError(error);
  }
};

/** Start authenticator-app setup during login: returns QR code data. */
export const getMFAPendingAppSetup = (mfa_token: string) =>
  apiGet<MFASetupData>(`${PENDING_BASE}/setup-app/`, { params: { mfa_token } });

/** Completes login in one step: confirms the device and issues the auth token. */
export type MFAPendingAppSetupConfirmResponse = AuthTokenResponse &
  MFAConfirmResponse;

export const confirmMFAPendingAppSetup = async (
  mfa_token: string,
  otp_token: string,
): Promise<
  MFAPendingAppSetupConfirmResponse | AuthErrorResponse | undefined
> => {
  try {
    return await apiPost<MFAPendingAppSetupConfirmResponse>(
      `${PENDING_BASE}/setup-app/`,
      { mfa_token, otp_token },
    );
  } catch (error: unknown) {
    return catchAuthError(error);
  }
};
